import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

import certCisco from './images/cert_cisco.png';
import certIbm from './images/cert_ibm.png';
import certPlp from './images/cert_plp.png';
import certSpss from './images/cert_spss.png';
import certCovenant from './images/cert_covenant.jpg';

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, delay: i * 0.1, ease: 'easeOut' },
  }),
};

const certifications = [
  {
    title: 'Certificate of Achievement — Software Development',
    issuer: 'PLP Academy',
    icon: '🏆',
    color: '#06b6d4',
    desc: 'Professional certification in software development covering modern programming practices and project delivery.',
    image: certPlp,
  },
  {
    title: 'Get Connected',
    issuer: 'Cisco Networking Academy',
    icon: '🌐',
    color: '#2563eb',
    desc: 'Networking fundamentals covering internet technologies, connectivity and digital communication.',
    image: certCisco,
  },
  {
    title: 'AI Fundamentals',
    issuer: 'IBM SkillsBuild',
    icon: '🤖',
    color: '#7c3aed',
    desc: 'Core concepts of artificial intelligence, machine learning and practical AI applications.',
    image: certIbm,
  },
  {
    title: 'Statistical Package for Social Sciences (SPSS)',
    issuer: 'Kesap Research Center',
    icon: '📊',
    color: '#059669',
    desc: 'Hands-on training in data analysis, statistical testing and research interpretation using SPSS.',
    image: certSpss,
  },
  {
    title: 'STATA Statistical Package',
    issuer: 'Kesap Research Center',
    icon: '📈',
    color: '#d97706',
    desc: 'Advanced statistical analysis and data management using STATA for research and reporting.',
    image: certSpss,
  },
  {
    title: 'Computer Studies',
    issuer: 'Covenant Cyber',
    icon: '💻',
    color: '#e11d48',
    desc: 'Foundational training in computer applications, systems and digital literacy.',
    image: certCovenant,
  },
];

const CertModal = ({ cert, onClose }) => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/90 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-3xl bg-[#0d1427] border border-white/10 rounded-2xl overflow-hidden shadow-2xl"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
          <div>
            <h3 className="text-white font-bold text-sm">{cert.title}</h3>
            <p className="text-xs mt-0.5" style={{ color: cert.color }}>{cert.issuer}</p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg bg-white/[0.06] hover:bg-white/10 text-slate-400 hover:text-white flex items-center justify-center transition-all"
          >
            x
          </button>
        </div>

        {/* Image — protected */}
        <div
          className="relative select-none"
          onContextMenu={(e) => e.preventDefault()}
        >
          <img
            src={cert.image}
            alt={cert.title}
            draggable={false}
            className="w-full object-contain max-h-[70vh] pointer-events-none"
            style={{ userSelect: 'none', WebkitUserSelect: 'none' }}
          />
          {/* Invisible overlay to block right-click save */}
          <div className="absolute inset-0 z-10" onContextMenu={(e) => e.preventDefault()} />
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-white/10 flex items-center justify-between">
          <span className="text-xs text-slate-500 font-mono">Protected — viewing only</span>
          <div
            className="inline-flex items-center gap-1.5 text-xs font-mono px-3 py-1.5 rounded-full border"
            style={{ background: `${cert.color}10`, borderColor: `${cert.color}25`, color: cert.color }}
          >
            <span className="w-1.5 h-1.5 rounded-full bg-current" />
            Certified
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

const Certifications = () => {
  const [selectedCert, setSelectedCert] = useState(null);

  return (
    <div className="relative py-32 px-6 bg-[#0a0f1e] overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-cyan-500/40 to-transparent" />
      <div className="absolute top-20 right-0 w-72 h-72 bg-cyan-500/5 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-20 left-0 w-72 h-72 bg-blue-500/5 rounded-full blur-3xl pointer-events-none" />

      <div className="max-w-6xl mx-auto">

        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="flex items-center gap-4 mb-6"
        >
          <span className="font-mono text-cyan-400 text-sm">05.</span>
          <h2 className="text-3xl md:text-4xl font-black text-white">Certifications</h2>
          <div className="flex-1 h-px bg-gradient-to-r from-cyan-500/40 to-transparent" />
        </motion.div>

        <motion.p
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="text-slate-400 mb-16 max-w-xl"
        >
          Professional qualifications and courses that complement my degree.
        </motion.p>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {certifications.map((cert, i) => (
            <motion.div
              key={cert.title}
              variants={fadeUp}
              custom={i}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="group relative p-6 rounded-2xl bg-white/[0.02] border border-white/[0.06] hover:border-white/20 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl flex flex-col justify-between"
            >
              <div
                className="absolute top-0 left-0 right-0 h-0.5 rounded-t-2xl"
                style={{ background: `linear-gradient(90deg, transparent, ${cert.color}, transparent)` }}
              />

              <div>
                <div className="flex items-start gap-4 mb-4">
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl flex-shrink-0 border"
                    style={{ background: `${cert.color}15`, borderColor: `${cert.color}30` }}
                  >
                    {cert.icon}
                  </div>
                  <div>
                    <h3 className="text-white font-bold text-sm leading-tight">
                      {cert.title}
                    </h3>
                    <p className="text-xs font-mono mt-1" style={{ color: cert.color }}>
                      {cert.issuer}
                    </p>
                  </div>
                </div>

                <p className="text-slate-500 text-sm leading-relaxed mb-5">
                  {cert.desc}
                </p>
              </div>

              <div className="flex items-center justify-between">
                <div
                  className="inline-flex items-center gap-1.5 text-xs font-mono px-3 py-1.5 rounded-full border"
                  style={{ background: `${cert.color}10`, borderColor: `${cert.color}25`, color: cert.color }}
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-current" />
                  Certified
                </div>

                <button
                  onClick={() => setSelectedCert(cert)}
                  className="flex items-center gap-2 px-4 py-1.5 rounded-full border border-white/10 text-slate-400 hover:text-white hover:border-white/30 text-xs font-medium transition-all duration-200"
                >
                  <span>👁</span> View
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-cyan-500/20 to-transparent" />

      <AnimatePresence>
        {selectedCert && (
          <CertModal cert={selectedCert} onClose={() => setSelectedCert(null)} />
        )}
      </AnimatePresence>
    </div>
  );
};

export default Certifications;